import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, MenuItem, Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

const SENIORITY_OPTIONS = [
  'Junior', 'Medior', 'Senior', 'Lead', 'Manager', 'Director', 'C-level',
];

const TONE_OPTIONS = [
  'Professioneel & zakelijk',
  'Informeel & toegankelijk',
  'Inspirerend & visionair',
  'Analytisch & datagedreven',
  'Creatief & out-of-the-box',
  'Coachend & ondersteunend',
];

export default function Phase1RoleIntake({ initialPrompt, onSubmit }) {
  const [form, setForm] = useState({
    functie: '',
    branche: '',
    senioriteit: 'Senior',
    doelen: '',
    toneOfVoice: '',
    context: '',
    taken: '',
  });
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (initialPrompt) {
      setForm((prev) => ({ ...prev, functie: initialPrompt }));
    }
  }, [initialPrompt]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = () => {
    setTried(true);
    if (!form.functie.trim() || !form.branche.trim() || !form.doelen.trim()) return;
    onSubmit(form);
  };

  const showError = (field) => tried && !form[field].trim();

  return (
    <div className="gpt-phase">
      <div className="gpt-phase-header">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeRoundedIcon sx={{ color: '#6366f1' }} />
          Vertel ons over de rol
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Hoe meer context je geeft, hoe beter de GPT wordt afgestemd op jouw specifieke situatie.
        </Typography>
      </div>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Row 1: Functie + Branche */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Functie of type medewerker"
            placeholder="bijv. Communicatiemedewerker, Marketing Manager"
            value={form.functie}
            onChange={handleChange('functie')}
            inputProps={{ maxLength: 200 }}
            required
            error={showError('functie')}
            helperText={showError('functie') ? 'Vul een functie in' : ''}
            fullWidth
          />
          <TextField
            label="Branche of sector"
            placeholder="bijv. Tech, Zorg, Energie, Overheid"
            value={form.branche}
            onChange={handleChange('branche')}
            inputProps={{ maxLength: 200 }}
            required
            error={showError('branche')}
            helperText={showError('branche') ? 'Vul een branche in' : ''}
            fullWidth
          />
        </Box>

        {/* Row 2: Senioriteit + Tone */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            label="Niveau van senioriteit"
            value={form.senioriteit}
            onChange={handleChange('senioriteit')}
            fullWidth
          >
            {SENIORITY_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Tone of voice (optioneel)"
            value={form.toneOfVoice}
            onChange={handleChange('toneOfVoice')}
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">— Kies een stijl —</MenuItem>
            {TONE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Doelen */}
        <TextField
          label="Belangrijkste doelen"
          placeholder="Wat moet de GPT je helpen bereiken? Bijv. interne communicatiestrategie opzetten, contentkalenders maken, stakeholders adviseren..."
          value={form.doelen}
          onChange={handleChange('doelen')}
          inputProps={{ maxLength: 2000 }}
          required
          error={showError('doelen')}
          helperText={showError('doelen') ? 'Vul minimaal één doel in' : ''}
          multiline
          rows={3}
          fullWidth
        />

        {/* Organisatiecontext */}
        <TextField
          label="Organisatiecontext (optioneel)"
          placeholder="Bijv. 'Netbeheerder met 3000 medewerkers, focus op duurzaamheid en energietransitie'"
          value={form.context}
          onChange={handleChange('context')}
          inputProps={{ maxLength: 1000 }}
          multiline
          rows={2}
          fullWidth
        />

        {/* Specifieke taken */}
        <TextField
          label="Specifieke taken waar de GPT goed in moet zijn (optioneel)"
          placeholder="Bijv. CEO-boodschappen schrijven, interne nieuwsbrieven, eventcommunicatie, gedragsveranderingscampagnes"
          value={form.taken}
          onChange={handleChange('taken')}
          inputProps={{ maxLength: 2000 }}
          multiline
          rows={2}
          fullWidth
        />

        {/* Submit - always enabled, validates on click */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ px: 4 }}
          >
            Experts ontdekken
          </Button>
        </Box>
      </Box>
    </div>
  );
}
